import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
	Button,
	Flex,
	FormControl,
	FormHelperText,
	FormLabel,
	Grid,
	Input,
	InputGroup,
	InputRightAddon,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Tooltip,
	useDisclosure,
	useToast,
	Box,
	Text,
	Icon,
} from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";
import { FaMapMarkerAlt, FaKey, FaUser, FaUserTie, FaPiggyBank, FaPlus } from "react-icons/fa";

import { createPropertyAsync, selectActiveProperties } from "../../redux/user/userSlice";
import { addPropertyVerifier } from "../../utils/verifier";

export default function NewPropertyModal() {
	const [error, setError] = useState({});
	const { t } = useTranslation();
	const currentUserEmail = useSelector((state) => state.auth.currentUserEmail);
	const properties = useSelector(selectActiveProperties);
	const { isOpen, onOpen, onClose } = useDisclosure();

	const owners = properties.map((property) => property.owner).filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i);
	const billingInformation = useSelector((state) => state.user.user?.billingInformation);

	const initialPropertyInformation = {
		owner: { name: "", email: "", phoneNumber: "" },
		bankDetails: { administrationFeePercent: "", currency: "", account: "" },
		location: {
			apartmentNumber: "",
			area: "",
			blockNumber: "",
			buildingNumber: "",
			city: "",
			county: "",
			entrance: "",
			street: "",
		},
		keys: "",
		agent: currentUserEmail,
	};
	const toast = useToast();
	const dispatch = useDispatch();
	const handleChange = (e) => {
		setpropertyInformation({ ...propertyInformation, [e.target.name]: e.target.value });
	};
	const handleClose = () => {
		onClose();
		setpropertyInformation(initialPropertyInformation);
		setError({});
	};
	const handleSave = () => {
		console.log(propertyInformation);
		const errors = addPropertyVerifier(propertyInformation);
		console.log(errors);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		dispatch(createPropertyAsync(propertyInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Property added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				setpropertyInformation(initialPropertyInformation);
				onClose();
			});
	};
	const [propertyInformation, setpropertyInformation] = useState(initialPropertyInformation);
	const renderAddPropertyModal = () => (
		<Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose} size="6xl">
			<ModalOverlay backdropFilter="blur(2px)" />
			<ModalContent>
				<ModalHeader borderBottom="1px solid" borderColor="gray.200" py={4}>
					<Flex align="center" gap={2}>
						<Text fontSize="xl">{t("properties.addProperty")}</Text>
					</Flex>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody pb={6}>
					<Stack spacing={6} py={4}>
						{/* Location Section */}
						<Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
							<Flex align="center" mb={4} gap={2}>
								<Icon as={FaMapMarkerAlt} color="teal.500" />
								<Text fontWeight="bold" fontSize="lg">
									{t("properties.location")}
								</Text>
							</Flex>
							<Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
								<FormControl isRequired>
									<FormLabel>{t("properties.street")}</FormLabel>
									<Input
										isInvalid={error?.location?.street}
										name="street"
										value={propertyInformation?.location.street}
										onChange={(e) => {
											setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, street: e.target.value } });
										}}
										_placeholder={{ color: "gray.400" }}
									/>
								</FormControl>
								<Grid templateColumns="1fr 1fr" gap={4}>
									<FormControl isRequired>
										<FormLabel>{t("properties.buildingNumber")}</FormLabel>
										<Input
											name="buildingNumber"
											value={propertyInformation?.location.buildingNumber}
											isInvalid={error?.location?.buildingNumber}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, buildingNumber: e.target.value } });
											}}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>{t("properties.blockNumber")}</FormLabel>
										<Input
											name="blockNumber"
											value={propertyInformation?.location.blockNumber}
											isInvalid={error?.location?.blockNumber}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, blockNumber: e.target.value } });
											}}
										/>
									</FormControl>
								</Grid>
								<Grid templateColumns="1fr 1fr" gap={4}>
									<FormControl isRequired>
										<FormLabel>{t("properties.apartmentNumber")}</FormLabel>
										<Input
											name="apartmentNumber"
											value={propertyInformation?.location.apartmentNumber}
											isInvalid={error?.location?.apartmentNumber}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, apartmentNumber: e.target.value } });
											}}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>{t("properties.entrance")}</FormLabel>
										<Input
											name="entrance"
											value={propertyInformation?.location.entrance}
											isInvalid={error?.location?.entrance}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, entrance: e.target.value } });
											}}
										/>
									</FormControl>
								</Grid>
								<FormControl isRequired>
									<FormLabel>{t("properties.area")}</FormLabel>
									<Input
										name="area"
										value={propertyInformation?.location.area}
										isInvalid={error?.location?.area}
										onChange={(e) => {
											setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, area: e.target.value } });
										}}
									/>
								</FormControl>
								<Grid templateColumns="1fr 1fr" gap={4}>
									<FormControl isRequired>
										<FormLabel>{t("properties.city")}</FormLabel>
										<Input
											name="city"
											value={propertyInformation?.location.city}
											isInvalid={error?.location?.city}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, city: e.target.value } });
											}}
										/>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>{t("properties.county")}</FormLabel>
										<Input
											name="county"
											value={propertyInformation?.location.county}
											isInvalid={error?.location?.county}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, location: { ...propertyInformation.location, county: e.target.value } });
											}}
										/>
									</FormControl>
								</Grid>
							</Grid>
						</Box>

						{/* Keys & Agent Section */}
						<Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={6}>
							<Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
								<Flex align="center" mb={4} gap={2}>
									<Icon as={FaKey} color="teal.500" />
									<Text fontWeight="bold" fontSize="lg">
										{t("properties.keys")}
									</Text>
								</Flex>
								<FormControl isRequired>
									<Input name="keys" placeholder={t("properties.keys")} isInvalid={error?.keys} value={propertyInformation?.keys} onChange={handleChange} />
								</FormControl>
							</Box>

							<Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
								<Flex align="center" mb={4} gap={2}>
									<Icon as={FaUserTie} color="teal.500" />
									<Text fontWeight="bold" fontSize="lg">
										{t("properties.agent")}
									</Text>
								</Flex>
								<FormControl isRequired>
									<Input isDisabled name="agent" value={propertyInformation?.agent} onChange={handleChange} type="email" />
								</FormControl>
							</Box>
						</Grid>

						{/* Bank Details Section */}
						<Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
							<Flex align="center" mb={4} gap={2}>
								<Icon as={FaPiggyBank} color="teal.500" />
								<Text fontWeight="bold" fontSize="lg">
									{t("properties.bankDetails")}
								</Text>
							</Flex>
							<Grid templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }} gap={4}>
								<FormControl isRequired>
									<FormLabel>{t("properties.iban")}</FormLabel>
									<Input
										name="iban"
										isInvalid={error?.bankDetails?.account}
										value={propertyInformation?.bankDetails.account}
										onChange={(e) => {
											setpropertyInformation({ ...propertyInformation, bankDetails: { ...propertyInformation.bankDetails, account: e.target.value } });
										}}
									/>
								</FormControl>
								<FormControl isRequired>
									<FormLabel>{t("properties.administrationFeePercent")}</FormLabel>
									<InputGroup>
										<Input
											name="administrationFeePercent"
											isInvalid={error?.bankDetails?.administrationFeePercent}
											value={propertyInformation?.bankDetails.administrationFeePercent}
											onChange={(e) => {
												setpropertyInformation({
													...propertyInformation,
													bankDetails: { ...propertyInformation.bankDetails, administrationFeePercent: e.target.value },
												});
											}}
											type="number"
										/>
										<InputRightAddon>%</InputRightAddon>
									</InputGroup>
								</FormControl>
								<FormControl isRequired>
									<FormLabel>{t("properties.currency")}</FormLabel>
									<Select
										name="currency"
										isInvalid={error?.bankDetails?.currency}
										value={propertyInformation?.bankDetails.currency}
										onChange={(e) => {
											setpropertyInformation({ ...propertyInformation, bankDetails: { ...propertyInformation.bankDetails, currency: e.target.value } });
										}}
									>
										<option value="eur">EUR</option>
										<option value="ron">RON</option>
									</Select>
								</FormControl>
							</Grid>
						</Box>

						{/* Owner Section */}
						<Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
							<Flex align="center" mb={4} gap={2}>
								<Icon as={FaUser} color="teal.500" />
								<Text fontWeight="bold" fontSize="lg">
									{t("properties.owner")}
								</Text>
							</Flex>
							<Stack spacing={4}>
								<ChakraSelect
									options={owners.map((o) => ({
										label: o.name,
										value: o,
									}))}
									onChange={(own) => {
										setpropertyInformation({
											...propertyInformation,
											owner: {
												...propertyInformation.owner,
												email: own.value.email,
												name: own.value.name,
												phoneNumber: own.value.phoneNumber,
											},
											bankDetails: {
												...propertyInformation.bankDetails,
												account: own.value.bankDetails.account,
												currency: own.value.bankDetails.currency,
												administrationFeePercent: own.value.bankDetails.administrationFeePercent,
											},
										});
									}}
								/>
								<Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
									<FormControl isRequired>
										<FormLabel>{t("properties.name")}</FormLabel>
										<Input
											name="name"
											isInvalid={error?.owner?.name}
											value={propertyInformation?.owner.name}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, owner: { ...propertyInformation.owner, name: e.target.value } });
											}}
										/>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>{t("properties.phoneNumber")}</FormLabel>
										<Input
											name="phoneNumber"
											isInvalid={error?.owner?.phoneNumber}
											value={propertyInformation?.owner.phoneNumber}
											onChange={(e) => {
												setpropertyInformation({ ...propertyInformation, owner: { ...propertyInformation.owner, phoneNumber: e.target.value } });
											}}
										/>
									</FormControl>
								</Grid>
								<FormControl isRequired>
									<FormLabel>{t("properties.email")}</FormLabel>
									<Input
										name="email"
										isInvalid={error?.owner?.email}
										value={propertyInformation?.owner.email}
										onChange={(e) => {
											setpropertyInformation({ ...propertyInformation, owner: { ...propertyInformation.owner, email: e.target.value } });
										}}
										type="email"
									/>
									<FormHelperText>Important: Verify the email carefully; it can't be changed later.</FormHelperText>
								</FormControl>
							</Stack>
						</Box>
					</Stack>
				</ModalBody>

				<ModalFooter borderTop="1px solid" borderColor="gray.200" py={4}>
					<Button colorScheme="blue" mr={3} onClick={handleSave} size="lg">
						{t("misc.save")}
					</Button>
					<Button onClick={handleClose} colorScheme="red" variant="outline" size="lg">
						{t("misc.cancel")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
	// const renderUploadContractModal = () => {};
	return (
		<>
			<Stack direction={"row"}>
				<Tooltip label="Add billing information to the agency" isDisabled={Object.keys(billingInformation).length !== 0}>
					<Button onClick={onOpen} colorScheme={"teal"} isDisabled={Object.keys(billingInformation).length === 0} size="md" leftIcon={<Icon as={FaPlus} />}>
						Add property
					</Button>
				</Tooltip>
				{/* <Tooltip label="Add billing information to the agency" isDisabled={Object.keys(billingInformation).length != 0}>
					<Button colorScheme={"teal"} onClick={onUploadContractOpen} isDisabled={Object.keys(billingInformation).length === 0}>
						Upload Smart Contract
					</Button>
				</Tooltip> */}
			</Stack>
			{renderAddPropertyModal()}
		</>
	);
}
